/**
 * Picking a simulation backend.
 *
 * Worker for the global graph: a 2000-node tick is 3 to 8ms and would blow the
 * frame budget alongside the canvas draw, React, and any Yjs or IndexedDB
 * work. Main thread for the local graph: 5 to 50 nodes ticks in under 50
 * microseconds and a second worker is pure overhead.
 *
 * Both sit behind SimHandle so nothing above here knows which it has, and a
 * worker that fails to construct falls back rather than breaking the view.
 */
import { SimEngine } from "./engine";
import { LOCAL_SIM_MAX, type SimHandle, type SimRequest, type SimResponse } from "./protocol";

class WorkerSim implements SimHandle {
  private worker: Worker;
  private cb: ((msg: SimResponse) => void) | null = null;

  constructor() {
    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.onmessage = (e: MessageEvent<SimResponse>) => this.cb?.(e.data);
  }

  send(msg: SimRequest, transfer?: Transferable[]): void {
    if (transfer && transfer.length) this.worker.postMessage(msg, transfer);
    else this.worker.postMessage(msg);
  }

  onMessage(cb: (msg: SimResponse) => void): void {
    this.cb = cb;
  }

  detach(): void {
    this.cb = null;
    try {
      this.worker.postMessage({ type: "stop" } satisfies SimRequest);
    } catch {
      // already gone
    }
    this.worker.terminate();
  }
}

/**
 * Main-thread backend. Ticking is driven by the caller's render loop through
 * pump(), so there is no second timer competing with rAF.
 */
class MainSim implements SimHandle {
  private engine: SimEngine;
  private cb: ((msg: SimResponse) => void) | null = null;

  constructor() {
    this.engine = new SimEngine((msg) => this.cb?.(msg), "copy");
  }

  send(msg: SimRequest): void {
    switch (msg.type) {
      case "setGraph":
        this.engine.setGraph(msg);
        break;
      case "forces":
        this.engine.setForces(msg.forces);
        break;
      case "active":
        this.engine.setActive(msg.activeNodes, msg.activeLinks, msg.warm);
        break;
      case "drag":
        this.engine.drag(msg.index, msg.x, msg.y);
        break;
      case "dragEnd":
        this.engine.dragEnd(msg.index);
        break;
      case "reheat":
        this.engine.reheat(msg.alpha, msg.target);
        break;
      case "recycle":
        break;
      case "stop":
        this.engine.destroy();
        break;
    }
  }

  onMessage(cb: (msg: SimResponse) => void): void {
    this.cb = cb;
  }

  /** Called once per render frame by the local graph's loop. */
  pump(budgetMs: number): void {
    const t0 = performance.now();
    let ticked = false;
    while (performance.now() - t0 < budgetMs && this.engine.needsTick()) {
      this.engine.tick();
      ticked = true;
    }
    if (ticked) this.engine.publish();
    this.engine.maybeSettled();
  }

  detach(): void {
    this.cb = null;
    this.engine.destroy();
  }
}

export function createSim(estimatedNodes: number): SimHandle {
  if (estimatedNodes > LOCAL_SIM_MAX && typeof Worker !== "undefined") {
    try {
      return new WorkerSim();
    } catch {
      // locked-down context: the main-thread engine is a real fallback, not a
      // stub, so there is no worker-only capability anywhere (invariant 6)
      return new MainSim();
    }
  }
  return new MainSim();
}

export { MainSim };
export type { SimHandle } from "./protocol";
